'use client';

import { useAuth, useOrganization, useUser } from '@clerk/clerk-react';
import { createRoute, useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';

import { BillingDetailsCard } from '@/components/billing/BillingDetailsCard';
import { CancelSubscriptionDialog } from '@/components/billing/CancelSubscriptionDialog';
import { InvoicesTable } from '@/components/billing/InvoicesTable';
import { SubscriptionCard } from '@/components/billing/SubscriptionCard';
import { UpdatePaymentMethodModal } from '@/components/billing/UpdatePaymentMethodModal';
import { CITokensCard } from '@/components/ci-tokens/CITokensCard';
import { InstallGithubApp } from '@/components/dashboard/InstallGithubApp';
import { SelectOrganization } from '@/components/dashboard/SelectOrganization';
import { SignInUnavailable } from '@/components/dashboard/SignInUnavailable';
import DashboardLayout from '@/components/layouts/DashboardLayout';
import { InviteMemberModal } from '@/components/members/InviteMemberModal';
import { MembersTable } from '@/components/members/MembersTable';
import { RemoveMemberModal } from '@/components/members/RemoveMemberModal';
import { RevokeInvitationModal } from '@/components/members/RevokeInvitationModal';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@/components/ui';
import { ORG_ROLES } from '@/lib/constants';
import { decideDashboardState } from '@/lib/organization/dashboardState';
import { useActiveOrganizationStatus } from '@/lib/query/useActiveOrganizationStatus';
import { useBillingSummary } from '@/lib/query/useBillingSummary';
import { useInstallationStatus } from '@/lib/query/useInstallationStatus';
import { useInvitations } from '@/lib/query/useInvitations';
import { useInvoices } from '@/lib/query/useInvoices';
import {
  Member,
  useOrganizationMembers,
} from '@/lib/query/useOrganizationMembers';
import { usePaymentMethod } from '@/lib/query/usePaymentMethod';
import { useRemoveMember } from '@/lib/query/useRemoveMember';
import { useResendInvitation } from '@/lib/query/useResendInvitation';
import { useRevokeInvitation } from '@/lib/query/useRevokeInvitation';
import { useSubscription } from '@/lib/query/useSubscription';
import { useUpdateMemberRole } from '@/lib/query/useUpdateMemberRole';
import { seoHead } from '@/lib/seo/routeMeta';
import { theme } from '@/lib/ui/theme';

import { Route as rootRoute } from './__root';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  head: () => seoHead('/dashboard'),
  component: DashboardPage,
});

function DashboardPage() {
  const { isSignedIn, isLoaded, has } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();

  // Data hooks — pulled in from previously-separate /billing and /members
  // routes. Dashboard is now the single authed page.
  const { data: installationStatus, isLoading: isLoadingStatus } =
    useInstallationStatus();
  const { organization: activeOrganization } = useOrganization();
  const { data: billing, isLoading: isLoadingBilling } = useBillingSummary();
  const { data: subscription, isLoading: isLoadingSubscription } =
    useSubscription();
  const { data: paymentMethod, isLoading: isLoadingPaymentMethod } =
    usePaymentMethod();
  const {
    data: invoicesData,
    isLoading: isLoadingInvoices,
    hasNextPage: hasMoreInvoices,
    fetchNextPage: fetchMoreInvoices,
    isFetchingNextPage: isFetchingMoreInvoices,
  } = useInvoices();
  const {
    data: membersData,
    isLoading: isLoadingMembers,
    hasNextPage: hasMoreMembers,
    fetchNextPage: fetchMoreMembers,
    isFetchingNextPage: isFetchingMoreMembers,
  } = useOrganizationMembers();

  const [fastPollInvites, setFastPollInvites] = useState(false);
  const { data: invitationsData } = useInvitations({
    fastPoll: fastPollInvites,
  });

  const removeMemberMutation = useRemoveMember();
  const updateRoleMutation = useUpdateMemberRole();
  const revokeInvitationMutation = useRevokeInvitation();
  const resendInvitationMutation = useResendInvitation();

  const isAdmin = has?.({ role: ORG_ROLES.ADMIN }) ?? false;
  const currentUserEmail = user?.primaryEmailAddress?.emailAddress;
  const members =
    membersData?.pages.flatMap((page) => page?.members ?? []) ?? [];

  // Pending members in the table are matched to their Clerk invitation by email (unique
  // per organization while pending) so the row's resend/revoke actions can target the invite.
  const invitationClerkIdByEmail = Object.fromEntries(
    (invitationsData?.invitations ?? []).map((inv) => [
      inv.email,
      inv.clerkInvitationId,
    ])
  );

  // Invite/resend persist the invitation row synchronously and refetch this list, so a
  // pending member normally has its id on the first render. fastPoll is a backstop: if a
  // row is ever left without its id (e.g. the synchronous write was lost and only the
  // webhook backs it), poll fast until resolved rather than waiting on the steady refetch.
  const hasUnresolvedPendingInvite = members.some(
    (m) => m.status === 'pending' && !invitationClerkIdByEmail[m.email]
  );
  useEffect(() => {
    setFastPollInvites(hasUnresolvedPendingInvite);
  }, [hasUnresolvedPendingInvite]);

  // Modal state
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<Member | null>(null);
  const [invitationToRevoke, setInvitationToRevoke] = useState<{
    member: Member;
    clerkInvitationId: string;
  } | null>(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  // The dashboard shows its full operating UI only once the active organization is set up (GitHub
  // App installed AND a subscription). No install → the install card; install pending or
  // active-but-unsubscribed → redirect to /onboarding to finish setup (a multi-organization user
  // who didn't mean to set this organization up escapes via the organization switcher in the onboarding nav).
  //
  // Membership is the first input because everything downstream is meaningless until it settles,
  // and because it is bounded: it always resolves. A term that reads an organization id directly
  // would not be — a session can hold none permanently, and the page would never leave loading.
  const membership = useActiveOrganizationStatus();
  const dashboardState = decideDashboardState({
    membership,
    installStatus: installationStatus?.status,
    hasSubscription: subscription != null,
  });
  const onboardingHref = `/onboarding${
    installationStatus?.installationId
      ? `?installation_id=${installationStatus.installationId}`
      : ''
  }`;

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate({ to: '/sign-in', search: {} });
    }
  }, [isLoaded, isSignedIn, navigate]);

  const isLoading =
    !isLoaded ||
    dashboardState === 'loading' ||
    isLoadingStatus ||
    isLoadingBilling ||
    isLoadingSubscription ||
    isLoadingPaymentMethod ||
    isLoadingInvoices ||
    isLoadingMembers;

  // A pending install, or an active install with no subscription yet, finishes setup on
  // /onboarding. Gate on !isLoading so we never redirect before the subscription query
  // settles (which would otherwise bounce a ready organization during the load window).
  useEffect(() => {
    if (!isLoading && dashboardState === 'redirectOnboarding') {
      window.location.href = onboardingHref;
    }
  }, [isLoading, dashboardState, onboardingHref]);

  const handleRoleChange = (memberId: string, newRole: 'admin' | 'member') => {
    updateRoleMutation.mutate({ memberId, role: newRole });
  };

  const handleConfirmRemove = () => {
    if (memberToRemove) {
      removeMemberMutation.mutate(memberToRemove.id, {
        onSuccess: () => setMemberToRemove(null),
      });
    }
  };

  const handleResendInvite = (clerkInvitationId: string) => {
    resendInvitationMutation.mutate(clerkInvitationId);
  };

  const handleConfirmRevokeInvite = () => {
    if (invitationToRevoke) {
      revokeInvitationMutation.mutate(invitationToRevoke.clerkInvitationId, {
        onSuccess: () => setInvitationToRevoke(null),
      });
    }
  };

  const invoices =
    invoicesData?.pages.flatMap((page) => page?.invoices ?? []) ?? [];

  if (isLoading) {
    return (
      <DashboardLayout isLoading>
        <section className="px-6 py-12 md:px-16">
          <div className="mx-auto" style={{ maxWidth: theme.spacing.maxWidth }}>
            <div className="space-y-6">
              {/* Get the mind CLI card */}
              <Card>
                <CardHeader>
                  <Skeleton className="h-6 w-40" />
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-4">
                    <Skeleton className="h-4 w-5/6" />
                    <Skeleton className="h-9 w-36 rounded-md" />
                  </div>
                </CardContent>
              </Card>
              {/* Members */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-7 w-32" />
                    <Skeleton className="h-5 w-16 rounded-full" />
                  </div>
                  <Skeleton className="h-9 w-32 rounded-md" />
                </CardHeader>
                <CardContent className="pt-4">
                  <MembersTable members={[]} isLoading isAdmin={false} />
                </CardContent>
              </Card>
              {/* Billing skeletons are unconditional (NOT gated on isAdmin): isAdmin
                  resolves after the first paint, so gating here made the billing cards
                  pop in as a jarring second loading phase. The standard loaded view is
                  installed-with-billing, so mirror that stably. */}
              <div className="grid gap-6 md:grid-cols-2">
                <Card>
                  <CardHeader>
                    <Skeleton className="h-6 w-32" />
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="space-y-3">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between gap-4"
                        >
                          <Skeleton className="h-4 w-24" />
                          <Skeleton className="h-4 w-28" />
                        </div>
                      ))}
                      <div className="pt-4">
                        <Skeleton className="h-9 w-32 rounded-md" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <Skeleton className="h-6 w-36" />
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
                      <Skeleton className="h-10 w-20 shrink-0 rounded" />
                      <div className="space-y-1.5">
                        <Skeleton className="h-5 w-28" />
                        <Skeleton className="h-4 w-24" />
                      </div>
                    </div>
                    <div className="mt-4">
                      <Skeleton className="h-9 w-40 rounded-md" />
                    </div>
                  </CardContent>
                </Card>
              </div>
              {/* Invoices */}
              <InvoicesTable invoices={[]} isLoading />
            </div>
          </div>
        </section>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <section className="px-6 py-12 md:px-16">
        <div className="mx-auto" style={{ maxWidth: theme.spacing.maxWidth }}>
          <div className="space-y-6">
            {/*
             * Token Usage panel intentionally disabled for now (per product
             * direction). Re-enable when the token-balance product surface
             * is back on the roadmap.
             */}

            {/* A not-set-up organization shows the install card (nothing installed yet) or is
                redirected to /onboarding to finish setup (pending install, or active
                install with no subscription — handled by the redirect effect above).
                Members, billing, and invoices stay hidden until the organization is set up. */}
            {dashboardState === 'signInUnavailable' ? (
              <SignInUnavailable />
            ) : dashboardState === 'install' ? (
              <InstallGithubApp organizationName={activeOrganization?.name} />
            ) : dashboardState === 'selectOrganization' ? (
              <SelectOrganization />
            ) : dashboardState === 'ready' ? (
              <>
                {/* Post-onboarding handoff: review locally + wire the agent stop-hook,
                    alongside CI token management (admins) or info (members). */}
                <div className="grid gap-6 md:grid-cols-2">
                  <Card>
                    <CardHeader>
                      <CardTitle>Get the mind CLI</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="space-y-4">
                        <p
                          className="text-muted-foreground text-sm"
                          style={{ fontFamily: theme.fonts.body }}
                        >
                          Review code from your terminal and wire Mindrealm into
                          Claude Code, Codex, Kimi Code, or Antigravity CLI so
                          it reviews every change automatically. Install the
                          CLI, sign in, and run your first review.
                        </p>
                        {/* Cross-page link to the CLI download anchor; useScrollToHash
                          (mounted in the root) scrolls to it after the page loads. */}
                        <Button asChild variant="outline">
                          <a href="/getting-started#download">
                            Download the CLI
                          </a>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  <CITokensCard isAdmin={isAdmin} />
                </div>

                {/* 1. Members */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CardTitle>Members</CardTitle>
                      <Badge variant="secondary" data-testid="seat-count-badge">
                        {members.length}{' '}
                        {members.length === 1 ? 'seat' : 'seats'}
                      </Badge>
                    </div>
                    {isAdmin && (
                      <Button
                        variant="outline"
                        onClick={() => setIsInviteModalOpen(true)}
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Invite Member
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent className="pt-4">
                    <MembersTable
                      members={members}
                      isLoading={false}
                      isAdmin={isAdmin}
                      currentUserEmail={currentUserEmail}
                      onRoleChange={handleRoleChange}
                      onRemove={(m) => setMemberToRemove(m)}
                      invitationClerkIdByEmail={invitationClerkIdByEmail}
                      onResendInvite={handleResendInvite}
                      onRevokeInvite={(member, clerkInvitationId) =>
                        setInvitationToRevoke({ member, clerkInvitationId })
                      }
                      resendingInvitationId={
                        resendInvitationMutation.isPending
                          ? (resendInvitationMutation.variables ?? null)
                          : null
                      }
                      hasMore={hasMoreMembers}
                      onLoadMore={() => fetchMoreMembers()}
                      isFetchingMore={isFetchingMoreMembers}
                    />
                  </CardContent>
                </Card>

                {/* Billing management (Subscription, Payment Method, Invoices) is
                admin-only — members have no actionable role here, so the whole
                cards are hidden rather than shown read-only. */}
                {isAdmin && (
                  <>
                    {/* 2. Subscription (left) + 3. Payment Method (right) — side by side on md+ */}
                    <div className="grid gap-6 md:grid-cols-2">
                      <SubscriptionCard
                        subscription={subscription}
                        billing={billing}
                        onCancelSubscription={() => setIsCancelDialogOpen(true)}
                        isAdmin={isAdmin}
                      />
                      <BillingDetailsCard
                        paymentMethod={paymentMethod}
                        onUpdatePaymentMethod={() =>
                          setIsPaymentModalOpen(true)
                        }
                        isAdmin={isAdmin}
                      />
                    </div>

                    {/* 4. Invoices */}
                    <InvoicesTable
                      invoices={invoices}
                      isLoading={false}
                      hasMore={hasMoreInvoices}
                      onLoadMore={() => fetchMoreInvoices()}
                      isFetchingMore={isFetchingMoreInvoices}
                    />
                  </>
                )}
              </>
            ) : null}
          </div>
        </div>
      </section>

      <InviteMemberModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
      />

      <RemoveMemberModal
        isOpen={!!memberToRemove}
        onClose={() => setMemberToRemove(null)}
        member={memberToRemove}
        onConfirm={handleConfirmRemove}
        isPending={removeMemberMutation.isPending}
      />

      <RevokeInvitationModal
        isOpen={!!invitationToRevoke}
        onClose={() => setInvitationToRevoke(null)}
        member={invitationToRevoke?.member ?? null}
        onConfirm={handleConfirmRevokeInvite}
        isPending={revokeInvitationMutation.isPending}
      />

      <UpdatePaymentMethodModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
      />

      <CancelSubscriptionDialog
        isOpen={isCancelDialogOpen}
        onClose={() => setIsCancelDialogOpen(false)}
        subscription={subscription}
        trialEndedAt={billing?.trialEndedAt}
      />
    </DashboardLayout>
  );
}
